import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClockIcon, CheckIcon, CircleIcon, FileUpIcon, MailIcon, UploadIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Meter } from '../ui/Meter';
import { EmptyState } from '../ui/EmptyState';
import { myApplications, programmeById, universityProfileById } from '../../data/admissions';
import type { ApplicationDocument, StudentApplication } from '../../types/admissions';
const DOC_TONE: Record<ApplicationDocument['status'], 'ok' | 'brand' | 'warn'> = {
  verified: 'ok',
  uploaded: 'brand',
  missing: 'warn'
};
function readiness(application: StudentApplication): number {
  const checks = [...application.requirements.map(requirement => requirement.met), ...application.documents.map(document => document.status !== 'missing'), application.feeRecorded];
  return Math.round(checks.filter(Boolean).length / checks.length * 100);
}
export function ApplicationWorkspace() {
  const [activeId, setActiveId] = useState(myApplications[0]?.id ?? '');
  const active = myApplications.find(application => application.id === activeId);
  if (myApplications.length === 0 || !active) {
    return <div className="rounded-card border border-line bg-surface" data-id="element-1081" data-mp="1zrxuwhiwbdow">
        <EmptyState icon={<FileUpIcon className="h-4 w-4" data-id="element-1083" data-mp="1c2c169lalqoa" />} title="No applications yet" description="Save a programme from Discover and Campuzon will build its checklist, deadlines and document list here." data-id="element-1082" data-mp="1zqmwkx87yy56" />
      </div>;
  }
  const programme = programmeById(active.programmeId);
  const university = universityProfileById(active.universityId);
  const outstanding = active.requirements.filter(requirement => !requirement.met);
  const missingDocuments = active.documents.filter(document => document.status === 'missing');
  return <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]" data-id="element-1084" data-mp="od0xaj8nbg5x">
      <aside data-id="element-1085" data-mp="2oeh9vwiii5je">
        <ul className="space-y-2" data-id="element-1086" data-mp="2of0pv7olqjus">
          {myApplications.map(application => {
          const applicationProgramme = programmeById(application.programmeId);
          const applicationUniversity = universityProfileById(application.universityId);
          const selected = application.id === activeId;
          return <li key={application.id} data-id="element-1087" data-mp="25go3ulcldfhs">
                <button type="button" onClick={() => setActiveId(application.id)} aria-current={selected} className={`w-full rounded-card border px-4 py-3 text-left transition-colors duration-150 ease-out ${selected ? 'border-brand-700 bg-brand-50' : 'border-line bg-surface hover:border-line-strong'}`} data-id="element-1088" data-mp="25hpsxtqg02ii">
                  <p className="text-[13px] font-semibold text-ink" data-id="element-1089" data-mp="3uv2zzqpxw4nb">
                    {applicationProgramme?.title}
                  </p>
                  <p className="mt-0.5 text-[12px] text-ink-muted" data-id="element-1090" data-mp="8tnarz4dtmg7">
                    {applicationUniversity?.name}
                  </p>
                  <div className="mt-2 flex items-center gap-2" data-id="element-1091" data-mp="3v9q2bdjpuwk8">
                    <Badge tone={application.stage === 'Offer' ? 'ok' : application.stage === 'Submitted' ? 'brand' : application.stage === 'Declined' ? 'danger' : 'warn'} data-id="element-1092" data-mp="3v7wwg0gxggda">
                      {application.stage}
                    </Badge>
                    <span className="cz-num text-[11px] text-ink-subtle" data-id="element-1093" data-mp="38b3apfntt3sw">
                      {readiness(application)}% ready
                    </span>
                  </div>
                </button>
              </li>;
        })}
        </ul>
      </aside>

      <div className="space-y-6" data-id="element-1094" data-mp="1dm58ldztfp0k">
        <Panel data-id="element-1095" data-mp="1dj0v1q0gngx8">
          <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-1096" data-mp="1ik6q3uhlfg9x">
            <div className="min-w-0" data-id="element-1097" data-mp="1ijf33822aqv9">
              <h2 className="text-lg font-semibold tracking-tight text-ink" data-id="element-1098" data-mp="14kp57q7f4n5u">
                {programme?.title}
              </h2>
              <p className="mt-1 text-[13px] text-ink-muted" data-id="element-1099" data-mp="18kpmdilwjcgl">
                {university?.name} · {university?.city}, {university?.country}
              </p>
              <p className="cz-num mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-ink-muted" data-id="element-1100" data-mp="zl2jesdixylf">
                <span className="inline-flex items-center gap-1.5" data-id="element-1101" data-mp="kwy849gswpco">
                  <CalendarClockIcon className="h-3.5 w-3.5" data-id="element-1102" data-mp="kuljjdgjfgqn" />
                  {active.deadlineLabel}
                </span>
                <span data-id="element-1103" data-mp="bv88igcrmk3t">{active.feeLabel}</span>
                <span data-id="element-1104" data-mp="bwjn7b7r1c9p">{active.feeRecorded ? 'Fee recorded' : 'Fee not recorded'}</span>
              </p>
            </div>
            <div className="w-full sm:w-52" data-id="element-1105" data-mp="gwnty9iuusmq">
              <Meter value={readiness(active)} label="Application readiness" trailing={`${readiness(active)}%`} tone={readiness(active) > 70 ? 'ok' : 'warn'} data-id="element-1106" data-mp="3tdnms0uzkunv" />
            </div>
          </div>
          {(outstanding.length > 0 || missingDocuments.length > 0) && <p className="mt-4 rounded-lg border border-warn-100 bg-warn-50 px-3 py-2 text-[13px] leading-6 text-ink-muted" data-id="element-1107" data-mp="2a8oq7gol8ru">
              Outstanding before you can submit: {outstanding.length} requirement
              {outstanding.length === 1 ? '' : 's'} and {missingDocuments.length} document
              {missingDocuments.length === 1 ? '' : 's'}.
            </p>}
        </Panel>

        <div className="grid gap-6 sm:grid-cols-2" data-id="element-1108" data-mp="l0qblveu1a5a">
          <Panel title="Requirements" bodyClassName="p-0" data-id="element-1109" data-mp="yxmyjlrty7gw">
            <ul className="divide-y divide-line" data-id="element-1110" data-mp="yw2a8dw3zkrh">
              {active.requirements.map(requirement => <li key={requirement.label} className="flex gap-3 px-5 py-3" data-id="element-1111" data-mp="3nrjthrv1tuum">
                  {requirement.met ? <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-ok-700" data-id="element-1112" data-mp="3splcydwdzw6m" /> : <CircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle" data-id="element-1113" data-mp="mc5kagpcc0t" />}
                  <div className="min-w-0" data-id="element-1114" data-mp="5ec7g8gc1f01">
                    <p className="text-[13px] leading-5 text-ink" data-id="element-1115" data-mp="5f576pgvj48o">{requirement.label}</p>
                    {requirement.note && <p className="mt-0.5 text-[12px] text-ink-muted" data-id="element-1116" data-mp="fayqqst0fxzy">{requirement.note}</p>}
                  </div>
                </li>)}
            </ul>
          </Panel>

          <Panel title="Documents" bodyClassName="p-0" data-id="element-1117" data-mp="ocg5a6qrrrmj">
            <ul className="divide-y divide-line" data-id="element-1118" data-mp="o9tzabjj1k6f">
              {active.documents.map(document => <li key={document.label} className="flex items-center gap-3 px-5 py-3" data-id="element-1119" data-mp="2m4aovyx07mf8">
                  <div className="min-w-0 flex-1" data-id="element-1120" data-mp="2q5m73jdjt5de">
                    <p className="text-[13px] text-ink" data-id="element-1121" data-mp="2il24gk983hbh">{document.label}</p>
                    {document.updated && <p className="cz-num text-[12px] text-ink-subtle" data-id="element-1122" data-mp="2drqvacgxuoge">
                        Updated {document.updated}
                      </p>}
                  </div>
                  <Badge tone={DOC_TONE[document.status]} data-id="element-1123" data-mp="24twxex3izmr4">
                    {document.status === 'missing' ? 'Missing' : document.status === 'uploaded' ? 'Uploaded' : 'Verified'}
                  </Badge>
                  {document.status === 'missing' && <Button size="sm" variant="secondary" icon={<UploadIcon className="h-3.5 w-3.5" data-id="element-1125" data-mp="2nibt3xosfoy9" />} data-id="element-1124" data-mp="2njdcqqjvdjrl">
                      Upload
                    </Button>}
                </li>)}
            </ul>
          </Panel>
        </div>

        <div className="grid gap-6 sm:grid-cols-2" data-id="element-1126" data-mp="1c61667wq6n1q">
          <Panel title="Correspondence" data-id="element-1127" data-mp="3c5y05nxgqpa9">
            <p className="flex items-start gap-2 text-[13px] leading-6 text-ink-muted" data-id="element-1128" data-mp="3c4wdsnazygd8">
              <MailIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" data-id="element-1129" data-mp="3g9chchx51bpa" />
              {active.lastCorrespondence}
            </p>
            <p className="mt-2 text-[12px] text-ink-subtle" data-id="element-1130" data-mp="3773aq34zq2h6">Contact: {active.contact}</p>
          </Panel>
          <Panel title="Get help with this application" data-id="element-1131" data-mp="3uwxm0g9nvizb">
            <p className="text-[13px] leading-6 text-ink-muted" data-id="element-1132" data-mp="3uxh2dsjnxzb8">
              Verified providers in the Campuzon marketplace offer motivation letter review,
              application strategy and IELTS coaching for this kind of programme.
            </p>
            <Link to="/marketplace" className="mt-3 inline-block" data-id="element-1133" data-mp="3q575fsk4ryzg">
              <Button size="sm" variant="secondary" data-id="element-1134" data-mp="3q3me0bt44w5t">
                Browse application guidance
              </Button>
            </Link>
          </Panel>
        </div>
      </div>
    </div>;
}